/**
 * Choropleth layer.
 * Colors regions by observation count or species richness.
 * Uses Natural Earth country boundaries with proper point-in-polygon spatial join.
 */

import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

const BOUNDARIES_SOURCE = 'ne-boundaries';
const CHOROPLETH_LAYER = 'obs-choropleth';
const BOUNDARIES_OUTLINE = 'obs-choropleth-outline';
const POINTS_SOURCE = 'obs-choropleth-points';

// Natural Earth 50m countries GeoJSON
const BOUNDARIES_URL = 'https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_50m_admin_0_countries.geojson';

let boundariesLoaded = false;
let cachedBoundaries = null;
let countByCountry = {};
let currentColorBy = null;
let currentGeojson = null;

/**
 * Fetch and cache Natural Earth boundaries.
 * Returns cached data on subsequent calls.
 */
async function fetchBoundaries() {
  if (cachedBoundaries) return cachedBoundaries;
  try {
    const res = await fetch(BOUNDARIES_URL);
    if (!res.ok) throw new Error(`Boundaries fetch failed: ${res.status}`);
    cachedBoundaries = await res.json();
    return cachedBoundaries;
  } catch (err) {
    console.error('[choropleth] Failed to fetch boundaries:', err);
    return null;
  }
}

/**
 * Aggregate observations by country using point-in-polygon spatial join.
 *
 * @param {object} geojson - GeoJSON FeatureCollection of observation points
 * @param {object} boundaries - Natural Earth GeoJSON FeatureCollection
 * @param {string|null} colorBy - If set, count unique taxon values per region (species richness)
 * @returns {object} Map of country name → count
 */
function aggregateByRegion(geojson, boundaries, colorBy) {
  if (!geojson?.features?.length || !boundaries?.features?.length) return {};

  const counts = {};
  const taxaSets = {};

  for (const obs of geojson.features) {
    if (!obs.geometry) continue;

    let matched = false;
    for (const boundary of boundaries.features) {
      if (booleanPointInPolygon(obs.geometry, boundary.geometry)) {
        const name = boundary.properties?.name || 'Unknown';
        
        if (colorBy) {
          if (!taxaSets[name]) taxaSets[name] = new Set();
          const taxon = obs.properties?.[colorBy] ?? 'Unknown';
          taxaSets[name].add(taxon);
        } else {
          counts[name] = (counts[name] || 0) + 1;
        }
        
        matched = true;
        break;
      }
    }

    if (!matched) {
      const key = 'Other';
      if (colorBy) {
        const taxon = obs.properties?.[colorBy] ?? 'Unknown';
        if (!taxaSets[key]) taxaSets[key] = new Set();
        taxaSets[key].add(taxon);
      } else {
        counts[key] = (counts[key] || 0) + 1;
      }
    }
  }

  // If counting species richness, convert sets to counts
  if (colorBy) {
    for (const [name, taxa] of Object.entries(taxaSets)) {
      counts[name] = taxa.size;
    }
  }

  return counts;
}

export async function add(map, sourceId, geojson, colorBy) {
  currentGeojson = geojson;
  currentColorBy = colorBy;

  // Store the points source
  if (!map.getSource(POINTS_SOURCE)) {
    map.addSource(POINTS_SOURCE, {
      type: 'geojson',
      data: geojson
    });
  } else {
    map.getSource(POINTS_SOURCE).setData(geojson);
  }

  const boundaries = await fetchBoundaries();
  if (!boundaries) return;

  countByCountry = aggregateByRegion(geojson, boundaries, colorBy);
  await renderChoropleth(map, boundaries);
}

export function remove(map, sourceId) {
  if (map.getLayer(CHOROPLETH_LAYER)) map.removeLayer(CHOROPLETH_LAYER);
  if (map.getLayer(BOUNDARIES_OUTLINE)) map.removeLayer(BOUNDARIES_OUTLINE);
  if (map.getSource(BOUNDARIES_SOURCE)) map.removeSource(BOUNDARIES_SOURCE);
  if (map.getSource(POINTS_SOURCE)) map.removeSource(POINTS_SOURCE);
  
  boundariesLoaded = false;
  cachedBoundaries = null;
  countByCountry = {};
  currentColorBy = null;
  currentGeojson = null;
}

export async function updateColorBy(map, sourceId, geojson, colorBy) {
  currentGeojson = geojson;
  currentColorBy = colorBy;

  const boundaries = await fetchBoundaries();
  if (!boundaries) return;

  countByCountry = aggregateByRegion(geojson, boundaries, colorBy);
  
  // Update existing source data
  const enriched = enrichBoundaries(boundaries);
  const source = map.getSource(BOUNDARIES_SOURCE);
  if (source) {
    source.setData(enriched);
    updateFillColor(map);
  }
}

function enrichBoundaries(boundaries) {
  return {
    type: 'FeatureCollection',
    features: boundaries.features.map(f => ({
      type: 'Feature',
      geometry: f.geometry,
      properties: {
        ...f.properties,
        _obs_count: countByCountry[f.properties?.name || 'Unknown'] || 0
      }
    }))
  };
}

async function renderChoropleth(map, boundaries) {
  if (boundariesLoaded) {
    // Update existing source data
    const source = map.getSource(BOUNDARIES_SOURCE);
    if (source) {
      const enriched = enrichBoundaries(boundaries);
      source.setData(enriched);
      updateFillColor(map);
    }
    return;
  }

  const enriched = enrichBoundaries(boundaries);

  map.addSource(BOUNDARIES_SOURCE, {
    type: 'geojson',
    data: enriched
  });

  const maxCount = Math.max(1, ...Object.values(countByCountry));

  // Ensure strictly ascending values for interpolate expression
  const step1 = Math.max(2, Math.ceil(maxCount * 0.25));
  const step2 = Math.max(step1 + 1, Math.ceil(maxCount * 0.5));

  map.addLayer({
    id: CHOROPLETH_LAYER,
    type: 'fill',
    source: BOUNDARIES_SOURCE,
    paint: {
      'fill-color': [
        'interpolate', ['linear'],
        ['get', '_obs_count'],
        0, 'rgba(30, 30, 60, 0.3)',
        1, '#1a237e',
        step1, '#1565c0',
        step2, '#f9a825',
        maxCount, '#e6194b'
      ],
      'fill-opacity': 0.7
    }
  }, getFirstSymbolLayerId(map));

  map.addLayer({
    id: BOUNDARIES_OUTLINE,
    type: 'line',
    source: BOUNDARIES_SOURCE,
    paint: {
      'line-color': 'rgba(255,255,255,0.2)',
      'line-width': 1
    }
  }, CHOROPLETH_LAYER);

  boundariesLoaded = true;
}

function updateFillColor(map) {
  const maxCount = Math.max(1, ...Object.values(countByCountry));
  
  // Ensure strictly ascending values for interpolate expression
  const step1 = Math.max(2, Math.ceil(maxCount * 0.25));
  const step2 = Math.max(step1 + 1, Math.ceil(maxCount * 0.5));

  if (map.getLayer(CHOROPLETH_LAYER)) {
    map.setPaintProperty(CHOROPLETH_LAYER, 'fill-color', [
      'interpolate', ['linear'],
      ['get', '_obs_count'],
      0, 'rgba(30, 30, 60, 0.3)',
      1, '#1a237e',
      step1, '#1565c0',
      step2, '#f9a825',
      maxCount, '#e6194b'
    ]);
  }
}

function getFirstSymbolLayerId(map) {
  const layers = map.getStyle().layers;
  return layers.find(l => l.type === 'symbol')?.id;
}
